import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";

const TEAM_IMG =
  "https://images.unsplash.com/photo-1630487656049-6db93a53a7e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nfGVufDB8fHx8MTc3ODkzNjEwNHww&ixlib=rb-4.1.0&q=85";

export default function About() {
  const { t } = useLang();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden ring-1 ring-slate-200 corner-accent">
              <img
                src={TEAM_IMG}
                alt="Jaytech enterprise consulting team"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="hidden md:flex absolute -bottom-6 -right-6 lg:-right-10 bg-[#0B1120] text-white p-6 rounded-sm w-56 shadow-xl">
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-slate-400">
                  Since
                </div>
                <div className="font-heading text-3xl font-extrabold mt-1">
                  Enterprise
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Indonesia-wide deployments
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#1E5BD6]">
              <span className="h-px w-8 bg-[#1E5BD6]" />
              {t.about.label}
            </div>

            <h2
              data-testid="about-title"
              className="mt-5 font-heading font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
            >
              {t.about.title}
            </h2>

            <div className="mt-6 space-y-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              <p>{t.about.body1}</p>
              <p>{t.about.body2}</p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {[t.about.pill1, t.about.pill2, t.about.pill3].map((pill) => (
                <span
                  key={pill}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700"
                  data-testid="about-pill"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#1E5BD6]" />
                  {pill}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
